<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">Configurações</h1>
          <div class="appui-crumb">App <span class="sep">/</span> Configurações</div>
        </div>
      </div>

      <!-- Acesso -->
      <section class="appui-card">
        <h2 class="appui-sec-title">Acesso</h2>
        <p class="appui-sec-sub">Tipo de acesso e regras de entrada no app.</p>
        <div class="appui-grid-3" style="margin-bottom:6px">
          <button v-for="m in loginModes" :key="m.v" class="appui-opt" :class="{ 'is-active': config.access_type === m.v }" @click="selectLogin(m.v)">
            <span class="dot"></span>
            <span class="appui-opt-title">{{ m.label }}</span>
            <span class="appui-opt-desc">{{ m.desc }}</span>
          </button>
        </div>

        <div class="appui-grid-2" style="margin-top:6px">
          <div class="appui-row">
            <div>
              <div class="appui-row-label">Acesso liberado?</div>
              <div class="appui-row-sub">Se suspenso, ninguém consegue logar no app.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.session_login }" @click="toggleAccount('session_login')"><span class="knob"></span></button>
          </div>
          <div class="appui-row">
            <div>
              <div class="appui-row-label">Cadastro liberado?</div>
              <div class="appui-row-sub">Participantes podem se cadastrar pelo app.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.session_register }" @click="toggleAccount('session_register')"><span class="knob"></span></button>
          </div>
          <div class="appui-row">
            <div>
              <div class="appui-row-label">RSVP Ativo?</div>
              <div class="appui-row-sub">Só participantes confirmados conseguem entrar.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.rsvp }" @click="toggleAccount('rsvp')"><span class="knob"></span></button>
          </div>
          <div class="appui-row">
            <div>
              <div class="appui-row-label">E-mail após cadastro</div>
              <div class="appui-row-sub">Enviar e-mail de boas-vindas ao se cadastrar.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.send_email_registration }" @click="toggleAccount('send_email_registration')"><span class="knob"></span></button>
          </div>
        </div>

        <div v-if="!account.session_login" class="appui-divider">
          <label class="appui-label">Mensagem de acesso bloqueado</label>
          <input class="appui-input" v-model="account.message_login_disabled" @blur="saveAccess" placeholder="Ex: O acesso ao app abre no dia do evento.">
        </div>
      </section>

      <!-- Experiência -->
      <section class="appui-card">
        <h2 class="appui-sec-title" style="margin-bottom:16px">Experiência</h2>
        <div class="appui-grid-2" style="gap:22px 32px">
          <div>
            <label class="appui-label">Tela inicial após login</label>
            <div class="appui-seg">
              <button :class="{ 'is-active': account.after_login === 1 }" @click="setAccount('after_login', 1)">Feed</button>
              <button :class="{ 'is-active': account.after_login === 2 }" @click="setAccount('after_login', 2)">Dashboard</button>
            </div>
          </div>
          <div>
            <label class="appui-label">Estilo do menu</label>
            <div class="appui-seg">
              <button :class="{ 'is-active': account.menu_style === 'list' }" @click="setAccount('menu_style', 'list')">Lista</button>
              <button :class="{ 'is-active': account.menu_style === 'grid' }" @click="setAccount('menu_style', 'grid')">Grade</button>
            </div>
          </div>
          <div class="appui-row" style="border:0;border-top:1px solid #eff2f7;border-radius:0;padding:16px 0 0">
            <div>
              <div class="appui-row-label">Carteira de pontos</div>
              <div class="appui-row-sub">Mostrar saldo de pontos no app.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.wallet_enabled }" @click="toggleAccount('wallet_enabled')"><span class="knob"></span></button>
          </div>
          <div class="appui-row" style="border:0;border-top:1px solid #eff2f7;border-radius:0;padding:16px 0 0">
            <div>
              <div class="appui-row-label">Completar perfil?</div>
              <div class="appui-row-sub">Bloquear até o participante preencher o perfil.</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': account.complete_profile }" @click="toggleAccount('complete_profile')"><span class="knob"></span></button>
          </div>
        </div>
      </section>

      <!-- Recursos -->
      <section class="appui-card">
        <h2 class="appui-sec-title">Recursos do app</h2>
        <p class="appui-sec-sub">Ligue apenas o que este evento vai usar.</p>
        <div class="appui-grid-2">
          <div v-for="f in featureRows" :key="f.key" class="appui-row">
            <div>
              <div class="appui-row-label">{{ f.label }}</div>
              <div class="appui-row-sub">{{ f.desc }}</div>
            </div>
            <button class="appui-switch" :class="{ 'is-on': interactions[f.key] }" @click="toggleFeature(f.key)"><span class="knob"></span></button>
          </div>
        </div>
      </section>

      <!-- Menu do app -->
      <section class="appui-card">
        <h2 class="appui-sec-title">Menu do app</h2>
        <p class="appui-sec-sub">Escolha os recursos que aparecem no menu. Use as setas para ordenar.</p>

        <div class="appui-sec-label">No menu</div>
        <div style="display:flex;flex-direction:column;gap:8px;margin-bottom:24px">
          <template v-for="(it, i) in menu">
            <div :key="it.id" class="appui-menu-row">
              <span class="appui-icon-chip"><i class="mdi" :class="itemIcon(it)"></i></span>
              <div style="flex:1;min-width:0">
                <div style="font-weight:500;font-size:13px;color:#495057">{{ it.name }}</div>
                <div class="appui-row-sub">{{ itemBadge(it) }}</div>
              </div>
              <button v-if="it.module === 'event.guide'" class="appui-icon-btn" @click="toggleExpand(it.id)"><i class="mdi" :class="expandedGuide === it.id ? 'mdi-chevron-up' : 'mdi-chevron-down'"></i></button>
              <button class="appui-switch" :class="{ 'is-on': it.status }" @click="toggleMenu(it)"><span class="knob"></span></button>
              <button class="appui-icon-btn" :disabled="i === 0" @click="moveMenu(i, -1)"><i class="mdi mdi-chevron-up"></i></button>
              <button class="appui-icon-btn" :disabled="i === menu.length - 1" @click="moveMenu(i, 1)"><i class="mdi mdi-chevron-down"></i></button>
              <button class="appui-icon-btn" @click="openRename(it)"><i class="mdi mdi-pencil"></i></button>
              <button class="appui-icon-btn appui-icon-btn--danger" @click="removeMenu(it)"><i class="mdi mdi-trash-can-outline"></i></button>
            </div>
            <!-- Sub-itens do Guia do evento -->
            <div v-if="it.module === 'event.guide' && expandedGuide === it.id" :key="'g' + it.id" style="margin:2px 0 6px 34px;display:flex;flex-direction:column;gap:6px">
              <div v-for="g in guideModules" :key="g.module" class="appui-menu-row" style="padding:8px 12px;background:#fbfcfe">
                <span class="appui-icon-chip" style="width:26px;height:26px;font-size:13px"><i class="mdi" :class="g.icon"></i></span>
                <span style="flex:1;font-size:12.5px;color:#495057">{{ childName(it, g) }}</span>
                <button class="appui-switch" :class="{ 'is-on': childActive(it, g) }" @click="toggleChild(it, g)"><span class="knob"></span></button>
              </div>
            </div>
          </template>
          <p v-if="!menu.length" class="appui-empty">Nenhum recurso no menu ainda. Adicione abaixo.</p>
        </div>

        <div class="appui-sec-label">Disponíveis para adicionar</div>
        <div class="appui-grid-3">
          <button v-for="m in availableModules" :key="m.module" class="appui-add-card" @click="addModule(m)">
            <span class="appui-icon-chip"><i class="mdi" :class="m.icon"></i></span>
            <span style="flex:1;min-width:0">
              <span class="appui-add-title">{{ m.label }}</span>
              <span class="appui-add-desc">{{ m.desc }}</span>
            </span>
            <i class="mdi mdi-plus" style="color:#556ee6;font-size:18px"></i>
          </button>
          <button class="appui-add-card" @click="openLink()">
            <span class="appui-icon-chip"><i class="mdi mdi-link-variant"></i></span>
            <span style="flex:1;min-width:0">
              <span class="appui-add-title">Link externo</span>
              <span class="appui-add-desc">Abrir uma página web no app</span>
            </span>
            <i class="mdi mdi-plus" style="color:#556ee6;font-size:18px"></i>
          </button>
        </div>
      </section>
    </div>

    <b-modal v-model="itemModal" :title="itemForm.id ? 'Editar item' : 'Novo link externo'" hide-footer>
      <form @submit.prevent="saveItem()">
        <div class="form-group"><label>Título *</label><input type="text" class="form-control" v-model="itemForm.name" required></div>
        <div v-if="itemForm.isLink" class="form-group"><label>Endereço (URL) *</label><input type="url" class="form-control" v-model="itemForm.link" placeholder="https://..." required></div>
        <div class="text-right">
          <button type="button" class="btn btn-light mr-2" @click="itemModal = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="savingItem">Salvar</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

export default {
  components: {},
  name: 'app-config',
  data () {
    return {
      config: { access_type: 3 },
      account: {
        session_register: false,
        session_login: true,
        rsvp: false,
        send_email_registration: false,
        message_login_disabled: '',
        after_login: 1,
        menu_style: 'list',
        wallet_enabled: false,
        complete_profile: false
      },
      interactions: { gamification: false, gamification_ranking: false, satisfaction_survey: false, certificate: false },
      menu: [],
      expandedGuide: null,
      itemModal: false,
      savingItem: false,
      itemForm: { id: null, name: '', link: '', isLink: false },
      loginModes: [
        { v: 3, label: 'Código por e-mail', desc: 'PIN enviado por e-mail, sem senha.' },
        { v: 2, label: 'E-mail', desc: 'Acesso direto informando o e-mail.' },
        { v: 1, label: 'E-mail e senha', desc: 'Login tradicional com senha.' }
      ],
      featureRows: [
        { key: 'gamification', label: 'Gamificação', desc: 'Pontos por interação.' },
        { key: 'gamification_ranking', label: 'Ranking', desc: 'Placar de líderes.' },
        { key: 'satisfaction_survey', label: 'Pesquisa', desc: 'Pesquisa de satisfação.' },
        { key: 'certificate', label: 'Certificado', desc: 'Emissão de certificado.' }
      ],
      // Catálogo de módulos nativos do app (plug-and-play).
      appModules: [
        { module: 'schedule', label: 'Programação', icon: 'mdi-calendar-clock', desc: 'Agenda e horários do evento' },
        { module: 'speakers', label: 'Palestrantes', icon: 'mdi-account-voice', desc: 'Palestrantes e biografias' },
        { module: 'sponsors', label: 'Patrocinadores', icon: 'mdi-storefront', desc: 'Patrocinadores e estandes' },
        { module: 'networking', label: 'Networking', icon: 'mdi-account-group', desc: 'Conexões entre participantes' },
        { module: 'videos', label: 'Vídeos', icon: 'mdi-play-circle', desc: 'Vídeos do evento' },
        { module: 'downloads', label: 'Arquivos', icon: 'mdi-folder-download', desc: 'Materiais para download' },
        { module: 'faq', label: 'Ajuda / FAQ', icon: 'mdi-help-circle', desc: 'Perguntas frequentes' },
        { module: 'ranking', label: 'Ranking', icon: 'mdi-trophy', desc: 'Ranking da gamificação' },
        { module: 'raffles', label: 'Sorteios', icon: 'mdi-gift', desc: 'Sorteios do evento' },
        { module: 'photo.booth', label: 'Cabine de fotos', icon: 'mdi-camera', desc: 'Cabine de fotos (web)' },
        { module: 'event.guide', label: 'Guia do evento', icon: 'mdi-map-marker-radius', desc: 'Hotel, mapa, transfer, Wi-Fi...' }
      ],
      // Sub-itens do Guia do evento.
      guideModules: [
        { module: 'event.guide.map', label: 'Mapa do evento', icon: 'mdi-map' },
        { module: 'event.guide.hotel', label: 'Hotel', icon: 'mdi-bed' },
        { module: 'event.guide.transfer', label: 'Transfer', icon: 'mdi-bus' },
        { module: 'event.guide.ticket', label: 'Passagens aéreas', icon: 'mdi-airplane' },
        { module: 'event.guide.wifi', label: 'Wi-Fi', icon: 'mdi-wifi' },
        { module: 'event.guide.activity', label: 'Atividades', icon: 'mdi-run' }
      ]
    }
  },
  computed: {
    availableModules () {
      const used = this.menu.map(it => it.module)
      return this.appModules.filter(m => !used.includes(m.module))
    }
  },
  mounted () {
    const loader = this.$loading.show()
    services.getAppSettings()
      .then(res => {
        const d = res.data.data
        this.config = { access_type: Number(d.access_type) }
        const a = d.account || {}
        this.account = {
          session_register: !!a.session_register,
          session_login: a.session_login === undefined ? true : !!a.session_login,
          rsvp: !!a.rsvp,
          send_email_registration: a.send_email_registration === undefined ? true : !!a.send_email_registration,
          message_login_disabled: a.message_login_disabled || '',
          after_login: Number(a.after_login) || 1,
          menu_style: a.menu_style || 'list',
          wallet_enabled: !!a.wallet_enabled,
          complete_profile: !!a.complete_profile
        }
        const it = d.interactions || {}
        Object.keys(this.interactions).forEach(k => { this.interactions[k] = !!it[k] })
      })
      .catch(() => errorMessage('Falha ao carregar configurações.', 3000))
      .finally(() => loader.hide())
    this.loadMenu()
  },
  methods: {
    /* ===== Acesso / Experiência ===== */
    selectLogin (v) { this.config.access_type = v; this.saveAccess() },
    setAccount (key, val) { this.account[key] = val; this.saveAccess() },
    toggleAccount (key) { this.account[key] = !this.account[key]; this.saveAccess() },
    async saveAccess () {
      try {
        await services.updateAccount({
          access_type: this.config.access_type,
          session_register: this.account.session_register ? 1 : 0,
          session_login: this.account.session_login ? 1 : 0,
          rsvp: this.account.rsvp ? 1 : 0,
          send_email_registration: this.account.send_email_registration ? 1 : 0,
          message_login_disabled: this.account.message_login_disabled,
          after_login: this.account.after_login,
          menu_style: this.account.menu_style,
          wallet_enabled: this.account.wallet_enabled,
          complete_profile: this.account.complete_profile ? 1 : 0
        })
        this.saved()
      } catch (e) { this.fail() }
    },
    async toggleFeature (key) {
      this.interactions[key] = !this.interactions[key]
      try {
        const payload = {}
        Object.keys(this.interactions).forEach(k => { payload[k] = this.interactions[k] ? 1 : 0 })
        await services.updateInteractions(payload)
        this.saved()
      } catch (e) { this.fail() }
    },
    /* ===== Menu ===== */
    loadMenu () { services.listMenu().then(r => { this.menu = r.data.data }).catch(() => {}) },
    itemIcon (it) {
      if (it.icon) return it.icon
      const m = this.appModules.find(x => x.module === it.module)
      return m ? m.icon : 'mdi-link-variant'
    },
    itemBadge (it) {
      if (!it.module) return 'Link externo'
      const m = this.appModules.find(x => x.module === it.module)
      return m ? m.label : 'Módulo'
    },
    async addModule (m) {
      const loader = this.$loading.show()
      try {
        const res = await services.createMenu({ name: m.label, module: m.module, icon: m.icon })
        // Guia do evento já nasce com os sub-itens ativos.
        if (m.module === 'event.guide') {
          const parentId = res.data.data.id
          for (const g of this.guideModules) {
            await services.createMenu({ name: g.label, module: g.module, icon: g.icon, ref_id: parentId })
          }
        }
        this.loadMenu(); this.saved()
      } catch (e) { this.fail() } finally { loader.hide() }
    },
    openLink () { this.itemForm = { id: null, name: '', link: '', isLink: true }; this.itemModal = true },
    openRename (it) { this.itemForm = { id: it.id, name: it.name, link: it.link || '', isLink: !it.module }; this.itemModal = true },
    async saveItem () {
      this.savingItem = true
      try {
        if (this.itemForm.id) {
          const p = { name: this.itemForm.name }
          if (this.itemForm.isLink) p.link = this.itemForm.link
          await services.updateMenu(this.itemForm.id, p)
        } else {
          await services.createMenu({ name: this.itemForm.name, link: this.itemForm.link, icon: 'mdi-link-variant' })
        }
        this.itemModal = false; this.loadMenu(); this.saved()
      } catch (e) { this.fail() } finally { this.savingItem = false }
    },
    async toggleMenu (it) { try { await services.changeMenuStatus(it.id); it.status = it.status ? 0 : 1 } catch (e) { this.fail() } },
    async removeMenu (it) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${it.name}" do menu?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteMenu(it.id); this.loadMenu() } catch (e) { this.fail() }
    },
    async moveMenu (index, dir) {
      const t = index + dir
      if (t < 0 || t >= this.menu.length) return
      const tmp = this.menu[index]; this.$set(this.menu, index, this.menu[t]); this.$set(this.menu, t, tmp)
      try { await services.reorderMenu(this.menu.map((it, i) => ({ id: it.id, sequence: i + 1 }))) } catch (e) { this.fail(); this.loadMenu() }
    },
    /* ===== Guia do evento (sub-itens) ===== */
    toggleExpand (id) { this.expandedGuide = this.expandedGuide === id ? null : id },
    guideChild (it, g) { return (it.children || []).find(c => c.module === g.module) },
    childActive (it, g) { const c = this.guideChild(it, g); return !!(c && c.status) },
    childName (it, g) { const c = this.guideChild(it, g); return c ? c.name : g.label },
    async toggleChild (it, g) {
      const c = this.guideChild(it, g)
      try {
        if (c) await services.changeMenuStatus(c.id)
        else await services.createMenu({ name: g.label, module: g.module, icon: g.icon, ref_id: it.id })
        this.loadMenu()
      } catch (e) { this.fail() }
    },
    saved () { successMessage('Salvo.', 1200) },
    fail () { errorMessage('Não foi possível salvar.', 3000) }
  }
}
</script>
